// The delivery note document (P4.2; 08-DOCUMENTS "Delivery note", mockup 15, D58). Same approach
// as the PO (D55): one bilingual renderer for the in-app print view and the HTML the broker
// converts to PDF; values escaped; classes only (the app's CSP has no inline styles). It reuses the
// PO document's styles and adds a few of its own.

import { unitText } from '../rfq/rfq';
import type { Unit } from '../pr/form';
import { both, esc, field, or, PO_CSS, qrSvg, stripeHtml, T } from '../po/document';
import { entityLogo } from '../po/logo';
import type { Condition } from './delivery';

export interface DnDocData {
  dnNo: string;
  /** YYYY-MM-DD, Riyadh */
  receivedOn: string;
  poNo: string;
  prNo: string;
  /** Delivery k on the PO. */
  seq: number;
  partial: boolean;
  entity: { code: string; nameEN: string; nameAR: string; cr: string; vat: string };
  supplier: { name: string; nameAR: string };
  supplierDnRef: string;
  /** The supplier's signed note is filed with the request. */
  supplierDnFiled: boolean;
  site: string;
  address: string;
  department: string;
  lines: {
    description: string;
    unit: string;
    ordered: number;
    thisDn: number;
    /** Good items received up to and including this delivery. */
    toDate: number;
    outstanding: number;
    /** Empty when nothing of the line arrived on this delivery. */
    condition: Condition | '';
  }[];
  receivedBy: {
    name: string;
    upn: string;
    at: string;
    onBehalf: boolean;
    by: string;
    /** Job title of the receiver, and the name of whoever confirmed for them (D104). */
    title?: string;
    byName?: string;
  };
  notes: string;
  verifyUrl: string;
}

const qty = (v: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(v);

const CONDITION: Record<Condition, [string, string]> = {
  good: ['Good', 'سليم'],
  damaged: ['Damaged', 'تالف'],
  wrong: ['Wrong item', 'صنف غير مطابق'],
};

/** The document (one page, body only). */
export function renderDnDocument(d: DnDocData): string {
  const e = d.entity;
  const HEAD_BG = `bgcolor="${T('accent')}"`;
  const open = d.lines.filter((l) => l.outstanding > 0).length;
  const lines = d.lines
    .map((l) => {
      const cond = l.condition ? CONDITION[l.condition] : null;
      return `<tr>
<td dir="auto">${esc(l.description)}<span class="pod-sub">${esc(unitText(l.unit as Unit, 'en'))}</span></td>
<td class="pod-num">${qty(l.ordered)}</td>
<td class="pod-num">${qty(l.thisDn)}</td>
<td class="pod-num">${qty(l.toDate)}</td>
<td class="pod-num">${qty(l.outstanding)}</td>
<td>${cond ? `<span class="${l.condition === 'good' ? '' : 'dnd-bad'}">${esc(cond[0])} · <span lang="ar">${esc(cond[1])}</span></span>` : '—'}</td>
</tr>`;
    })
    .join('');
  const summary = d.partial
    ? both(
        `<strong>Partial delivery · DN ${d.seq}.</strong> PO stays open for ${open} outstanding item${open === 1 ? '' : 's'}.`,
        `<strong>تسليم جزئي · السند ${d.seq}.</strong> يبقى أمر الشراء مفتوحاً لـ ${open} من الأصناف المتبقية.`,
      )
    : both(
        `<strong>Delivery complete · DN ${d.seq}.</strong> Everything on the PO has been received.`,
        `<strong>اكتمل التسليم · السند ${d.seq}.</strong> استُلم كل ما في أمر الشراء.`,
      );
  const by = d.receivedBy;

  return `<article class="pod dnd" lang="en">
<section class="pod-page">
${stripeHtml()}
<header class="pod-head">
<div class="pod-brand">${entityLogo(d.entity.code)}</div>
<div class="pod-title"><h1>DELIVERY NOTE</h1><p lang="ar" dir="rtl">سند استلام</p></div>
</header>
<div class="pod-entity">
<p>${or(e.nameEN, `[${e.code} legal name]`)} · CR ${or(e.cr, '[CR no.]')} · VAT ${or(e.vat, '[VAT no.]')}</p>
<p lang="ar" dir="rtl">${or(e.nameAR, '[الاسم القانوني]')}</p>
</div>
<div class="pod-refs">
${field('DN no.', 'رقم السند', `<strong>${esc(d.dnNo)}</strong>`)}
${field('Received on', 'تاريخ الاستلام', esc(d.receivedOn))}
${field('PO no.', 'رقم أمر الشراء', esc(d.poNo))}
${field('PR no.', 'رقم الطلب', esc(d.prNo))}
</div>
<div class="pod-parties">
<div class="pod-box"><h2>${both('SUPPLIER', 'المورد')}</h2>
${field('Name', 'الاسم', esc(d.supplier.name))}
${d.supplier.nameAR ? field('Arabic name', 'الاسم بالعربية', esc(d.supplier.nameAR)) : ''}
${field("Supplier's DN", 'سند المورد', `<span dir="ltr">${or(d.supplierDnRef, '—')}</span>${d.supplierDnFiled ? ' (attached · مرفق)' : ''}`)}
</div>
<div class="pod-box"><h2>${both('RECEIVED AT', 'موقع الاستلام')}</h2>
${field('Site', 'الموقع', `${or(d.site, '—')}${d.address ? ` · ${esc(d.address)}` : ''}`)}
${field('Department', 'الإدارة', or(d.department, '—'))}
${field('Receiver', 'المستلم', or(by.name, by.upn))}
</div>
</div>
<table class="pod-lines">
<thead><tr><th ${HEAD_BG}>${both('Item', 'الصنف')}</th><th ${HEAD_BG} class="pod-num">${both('Ordered', 'المطلوب')}</th><th ${HEAD_BG} class="pod-num">${both('This DN', 'هذا السند')}</th><th ${HEAD_BG} class="pod-num">${both('To date', 'حتى الآن')}</th><th ${HEAD_BG} class="pod-num">${both('Outstanding', 'المتبقي')}</th><th ${HEAD_BG}>${both('Condition', 'الحالة')}</th></tr></thead>
<tbody>${lines}</tbody>
</table>
<p class="dnd-summary ${d.partial ? 'dnd-summary--partial' : ''}">${summary}</p>
${d.notes ? `<p class="pod-extra dnd-notes" dir="auto"><span class="pod-label">Notes · <span lang="ar">ملاحظات</span></span> ${esc(d.notes)}</p>` : ''}
<footer class="pod-sign">
<div class="pod-sign__box pod-sign__box--ok"><span class="pod-label">RECEIVED BY · REQUESTER · <span lang="ar">المستلم</span></span><strong>${or(by.name, by.upn)}</strong>${by.title ? `<span>${esc(by.title)}</span>` : ''}<span>Confirmed in the portal · <span lang="ar">مؤكد في البوابة</span></span>${
    by.onBehalf
      ? `<span>Confirmed by Procurement (${esc(by.byName || by.by)}) on the requester's behalf · <span lang="ar">أكّدته المشتريات نيابةً عن مقدّم الطلب</span></span>`
      : ''
  }<span dir="ltr">${esc(by.at)}</span></div>
<div class="pod-sign__box"><span class="pod-label">FILED BY · PROCUREMENT · <span lang="ar">المشتريات</span></span><strong>Procure</strong><span>Filed with ${esc(d.prNo)} · <span lang="ar">محفوظ مع الطلب</span></span></div>
<div class="pod-qr-box">${qrSvg(d.verifyUrl)}<span dir="ltr">${esc(d.dnNo)}</span></div>
</footer>
<p class="pod-foot"><span>System-generated delivery note · <span lang="ar">سند استلام صادر من النظام</span></span><span dir="ltr">${esc(d.dnNo)}</span></p>
</section>
</article>`;
}

/** DN additions to the PO document styles. */
export const DN_CSS = `${PO_CSS}
.dnd .pod-lines td{font-size:10px}
.dnd-summary{display:flex;justify-content:space-between;gap:12px;margin:0;padding:6px 8px;border-radius:6px;background:${T('accent-soft')};color:${T('accent-ink')}}
.dnd-summary--partial{background:${T('warning-panel')};color:${T('warning-panel-ink')}}
.dnd-bad{color:${T('danger-ink')};font-weight:700}
.dnd-notes{display:flex;gap:8px;align-items:baseline}
`;

/** A complete HTML file for the PDF converter. */
export function dnHtmlFile(d: DnDocData): string {
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${esc(d.dnNo)}</title><style>${DN_CSS}</style></head><body>${renderDnDocument(d)}</body></html>`;
}
